import React from 'react';
import { Link } from 'react-router-dom';

export default function LegalCompliance() {
  return (
    <div className="bg-gray-50 min-h-screen">
      {/* Hero Section */}
      <section className="relative py-16 bg-gradient-to-br from-gray-900 to-gray-800 text-white">
        <div className="section-container">
          <div className="max-w-4xl">
            <h1 className="text-3xl md:text-4xl font-bold mb-4">Legal & Compliance</h1>
            <p className="text-lg text-gray-300">
              Official statements regarding MGL Experiences Ltd's business model, responsibilities, and partnership structure.
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <div className="section-container">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Sidebar - Navigation */}
          <div className="lg:col-span-1">
            <div className="sticky top-24">
              <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Legal Documents</h3>
                <nav className="space-y-2">
                  <Link to="/legal-compliance" className="block text-primary font-medium hover:text-primary-dark">
                    Legal & Compliance
                  </Link>
                  <Link to="/privacy-policy" className="block text-gray-600 hover:text-primary">
                    Privacy Policy
                  </Link>
                  <Link to="/terms-of-use" className="block text-gray-600 hover:text-primary">
                    Terms of Use
                  </Link>
                  <Link to="/partner-disclaimer" className="block text-gray-600 hover:text-primary">
                    Partner Disclaimer
                  </Link>
                </nav>
              </div>
              
              <div className="bg-white rounded-xl shadow-lg p-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4">Quick Reference</h3>
                <div className="space-y-3">
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3"></div>
                    <span className="text-sm text-gray-600">UK Company Registration</span>
                  </div>
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3"></div>
                    <span className="text-sm text-gray-600">IP Ownership Model</span>
                  </div>
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3"></div>
                    <span className="text-sm text-gray-600">Operator Partnership Structure</span>
                  </div>
                  <div className="flex items-start">
                    <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3"></div>
                    <span className="text-sm text-gray-600">Compliance Framework</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-xl shadow-lg overflow-hidden">
              {/* Header */}
              <div className="border-b border-gray-200 p-6">
                <div className="flex items-center mb-4">
                  <div className="w-10 h-10 bg-primary/10 rounded-lg flex items-center justify-center mr-4">
                    <svg className="w-6 h-6 text-primary" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2 4h-2v2h2V9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold text-gray-900">Legal & Compliance Notice</h2>
                    <p className="text-gray-600">Effective Date: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}</p>
                  </div>
                </div>
              </div>

              {/* Content */}
              <div className="p-6 md:p-8">
                {/* Company Overview */}
                <div className="mb-10">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Company Overview</h3>
                  <div className="prose prose-lg max-w-none">
                    <p className="text-gray-700 mb-4">
                      <strong>MGL Experiences Ltd</strong> is a company incorporated in the United Kingdom and operates as a travel events, experience design, and intellectual property company.
                    </p>
                    <p className="text-gray-700 mb-6">
                      The company specialises in:
                    </p>
                    <ul className="space-y-2 mb-6">
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3"></div>
                        <span className="text-gray-700">Designing destination-led travel experiences</span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3"></div>
                        <span className="text-gray-700">Owning and licensing travel event and experience formats</span>
                      </li>
                      <li className="flex items-start">
                        <div className="w-2 h-2 bg-primary rounded-full mt-2 mr-3"></div>
                        <span className="text-gray-700">Developing large-scale cultural and lifestyle travel concepts</span>
                      </li>
                    </ul>
                    <p className="text-gray-700">
                      MGL Experiences Ltd retains ownership, governance, and strategic oversight of its experience formats while working in collaboration with licensed travel operators for execution.
                    </p>
                  </div>
                </div>

                {/* No Retail Travel Services */}
                <div className="mb-10 p-6 bg-red-50 border-l-4 border-red-500 rounded-r-lg">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">No Retail Travel Services</h3>
                  <div className="prose prose-lg max-w-none">
                    <p className="text-gray-700 mb-4">
                      <strong>MGL Experiences Ltd does not sell travel tickets, accommodation, transportation, travel insurance, or any other retail travel services directly to consumers.</strong>
                    </p>
                    <p className="text-gray-700 mb-4">
                      All travel-related services connected to MGL Experiences projects are:
                    </p>
                    <div className="grid md:grid-cols-3 gap-4 mb-6">
                      <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                        <div className="text-2xl font-bold text-red-600 mb-2">Sold</div>
                        <p className="text-sm text-gray-600">By licensed travel operators</p>
                      </div>
                      <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                        <div className="text-2xl font-bold text-red-600 mb-2">Managed</div>
                        <p className="text-sm text-gray-600">By certified operator partners</p>
                      </div>
                      <div className="text-center p-4 bg-white rounded-lg shadow-sm">
                        <div className="text-2xl font-bold text-red-600 mb-2">Delivered</div>
                        <p className="text-sm text-gray-600">On-ground by execution teams</p>
                      </div>
                    </div>
                    <p className="text-gray-700">
                      by licensed and authorised third-party travel operators who act as execution partners.
                    </p>
                    <p className="text-gray-700 mt-4">
                      <strong>MGL Experiences Ltd does not collect traveller payments, issue tickets, or manage consumer bookings.</strong>
                    </p>
                  </div>
                </div>

                {/* Execution Partners */}
                <div className="mb-10">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Execution Partners & Responsibilities</h3>
                  <div className="prose prose-lg max-w-none">
                    <p className="text-gray-700 mb-4">
                      Where MGL Experiences Ltd collaborates with regional or international travel operators (including, but not limited to, Boundless Wander Wave), those operators are solely responsible for:
                    </p>
                    <div className="grid md:grid-cols-2 gap-6 mb-6">
                      <ul className="space-y-3">
                        <li className="flex items-start">
                          <svg className="w-5 h-5 text-primary mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          <span className="text-gray-700">Traveller bookings and reservations</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="w-5 h-5 text-primary mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          <span className="text-gray-700">Collection and processing of payments</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="w-5 h-5 text-primary mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          <span className="text-gray-700">Travel documentation and visa support (where applicable)</span>
                        </li>
                      </ul>
                      <ul className="space-y-3">
                        <li className="flex items-start">
                          <svg className="w-5 h-5 text-primary mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          <span className="text-gray-700">On-ground logistics and service delivery</span>
                        </li>
                        <li className="flex items-start">
                          <svg className="w-5 h-5 text-primary mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                            <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                          </svg>
                          <span className="text-gray-700">Customer service, support, and traveller communications</span>
                        </li>
                      </ul>
                    </div>
                    <p className="text-gray-700 font-medium">
                      MGL Experiences Ltd is not responsible for the operational performance of execution partners.
                    </p>
                  </div>
                </div>

                {/* Technology Platform */}
                <div className="mb-10 p-6 bg-blue-50 rounded-lg">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Technology Platform</h3>
                  <div className="prose prose-lg max-w-none">
                    <p className="text-gray-700 mb-4">
                      MGL Experiences Ltd utilises Travefy as an itinerary management and traveller communication platform to support the delivery of experiences.
                    </p>
                    <div className="flex items-center p-4 bg-white rounded-lg mb-4">
                      <div className="w-10 h-10 bg-blue-100 rounded-lg flex items-center justify-center mr-4">
                        <svg className="w-6 h-6 text-blue-600" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M11.3 1.046A1 1 0 0112 2v5h4a1 1 0 01.82 1.573l-7 10A1 1 0 018 18v-5H4a1 1 0 01-.82-1.573l7-10a1 1 0 011.12-.38z" clipRule="evenodd" />
                        </svg>
                      </div>
                      <div>
                        <h4 className="font-bold text-gray-900">Travefy Platform</h4>
                        <p className="text-sm text-gray-600">Technology provider for itinerary management</p>
                      </div>
                    </div>
                    <p className="text-gray-700">
                      Travefy is a technology provider only and does not sell, manage, or operate travel services. Its use is limited to itinerary presentation, documentation, and traveller updates.
                    </p>
                  </div>
                </div>

                {/* Limitation of Liability */}
                <div className="mb-10 p-6 bg-yellow-50 border-l-4 border-yellow-500 rounded-r-lg">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Limitation of Liability</h3>
                  <div className="prose prose-lg max-w-none">
                    <p className="text-gray-700 mb-4">
                      <strong>MGL Experiences Ltd shall not be liable for any loss, damage, disruption, or inconvenience arising from:</strong>
                    </p>
                    <ul className="space-y-3 mb-6">
                      <li className="flex items-start">
                        <svg className="w-5 h-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <span className="text-gray-700">Travel delays or cancellations</span>
                      </li>
                      <li className="flex items-start">
                        <svg className="w-5 h-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <span className="text-gray-700">Airline, accommodation, or transport provider issues</span>
                      </li>
                      <li className="flex items-start">
                        <svg className="w-5 h-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <span className="text-gray-700">Border control, immigration, or regulatory decisions</span>
                      </li>
                      <li className="flex items-start">
                        <svg className="w-5 h-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <span className="text-gray-700">Acts, omissions, or failures of execution partners</span>
                      </li>
                      <li className="flex items-start">
                        <svg className="w-5 h-5 text-yellow-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                        </svg>
                        <span className="text-gray-700">Force majeure events or third-party service failures</span>
                      </li>
                    </ul>
                    <p className="text-gray-700 font-medium">
                      Liability for travel services rests solely with the licensed travel operator providing those services.
                    </p>
                  </div>
                </div>

                {/* Governing Law */}
                <div className="mb-6 p-6 bg-gray-100 rounded-lg">
                  <h3 className="text-xl font-bold text-gray-900 mb-4">Governing Law</h3>
                  <div className="flex items-center">
                    <div className="w-10 h-10 bg-gray-200 rounded-lg flex items-center justify-center mr-4">
                      <svg className="w-6 h-6 text-gray-700" fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM4.332 8.027a6.012 6.012 0 011.912-2.706C6.512 5.73 6.974 6 7.5 6A1.5 1.5 0 019 7.5V8a2 2 0 004 0 2 2 0 011.523-1.943A5.977 5.977 0 0116 10c0 .34-.028.675-.083 1H15a2 2 0 00-2 2v2.197A5.973 5.973 0 0110 16v-2a2 2 0 00-2-2 2 2 0 01-2-2 2 2 0 00-1.668-1.973z" clipRule="evenodd" />
                      </svg>
                    </div>
                    <div>
                      <p className="text-gray-700 font-medium">
                        These terms and any related disputes shall be governed by and construed in accordance with the laws of England and Wales.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Footer Disclaimer */}
                <div className="mt-10 pt-8 border-t border-gray-200">
                  <h3 className="text-lg font-bold text-gray-900 mb-4">Footer Disclaimer (Short Form)</h3>
                  <div className="bg-gray-50 p-6 rounded-lg">
                    <p className="text-gray-700 italic">
                      "MGL Experiences Ltd is a UK-based travel events and experiences company. We do not operate as a retail travel agency. All experiences are delivered through licensed travel partners."
                    </p>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="border-t border-gray-200 p-6 bg-gray-50">
                <div className="flex flex-col md:flex-row md:items-center justify-between">
                  <div>
                    <p className="text-sm text-gray-600">
                      Last Updated: {new Date().toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' })}
                    </p>
                  </div>
                  <div className="mt-4 md:mt-0">
                    <Link
                      to="/contact"
                      className="text-sm text-primary font-medium hover:text-primary-dark"
                    >
                      Contact Legal Department →
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            {/* Note */}
            <div className="mt-8 p-4 bg-blue-50 rounded-lg">
              <div className="flex">
                <svg className="w-5 h-5 text-blue-600 mt-0.5 mr-3 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                </svg>
                <p className="text-sm text-gray-700">
                  This legal statement clarifies MGL Experiences Ltd's business model as an experience design and IP company. For specific legal inquiries, please contact our legal department.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}